// Category Tabs Component - Optimized for 50+ Categories with Search & Grid Modal
import { useState, useMemo, useRef, useEffect } from 'react';
import { Category } from '../types';

interface CategoryTabsProps {
  categories: { value: Category; label: string; icon: string }[];
  selected: Category;
  onSelect: (category: Category) => void;
  platformConfig: {
    bg: string;
    text: string;
  };
}

export default function CategoryTabs({ categories, selected, onSelect, platformConfig }: CategoryTabsProps) {
  const [showModal, setShowModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);
  
  const filteredCategories = useMemo(() => {
    const trimmed = searchQuery.trim();
    if (trimmed.length === 0) return categories;
    const query = trimmed.toLowerCase();
    return categories.filter(cat => 
      cat.label.toLowerCase().includes(query) || 
      cat.value.toLowerCase().includes(query)
    );
  }, [categories, searchQuery]);

  const visibleTabs = categories.slice(0, 8);
  const hiddenCategories = categories.slice(8);

  useEffect(() => {
    if (showModal && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 100);
    }
  }, [showModal]);

  const handleSelect = (category: Category) => {
    onSelect(category);
    setShowModal(false);
    setSearchQuery('');
  };

  return (
    <>
      <div className="flex items-center gap-2">
        <div className="overflow-x-auto flex-nowrap scrollbar-none pb-2 flex-1">
          <div className="flex gap-2 min-w-max">
            {visibleTabs.map(cat => (
              <button
                key={cat.value}
                onClick={() => onSelect(cat.value)}
                className={`px-3 md:px-4 py-1.5 md:py-2 rounded-lg font-medium text-xs md:text-sm transition-all whitespace-nowrap ${
                  selected === cat.value
                    ? `${platformConfig.bg} text-white shadow-lg`
                    : 'bg-slate-800 text-slate-300 hover:bg-slate-700 border border-slate-700'
                }`}
              >
                {cat.icon} <span className="hidden sm:inline">{cat.label}</span>
                <span className="sm:hidden">{cat.label.split(' ')[0]}</span>
              </button>
            ))}
          </div>
        </div>

        {hiddenCategories.length > 0 && (
          <button
            onClick={() => setShowModal(true)}
            className="flex-shrink-0 px-3 py-1.5 md:px-4 md:py-2 rounded-lg font-medium text-xs md:text-sm bg-slate-800 text-slate-300 hover:bg-slate-700 border border-slate-700 transition-all whitespace-nowrap"
          >
            📂 <span className="hidden sm:inline">Kategori Lainnya</span>
            <span className="sm:hidden">Lainnya</span>
            <span className="ml-1 px-1.5 py-0.5 bg-slate-700 rounded text-[10px]">
              +{hiddenCategories.length}
            </span>
          </button>
        )}
      </div>

      {showModal && (
        <>
          <div 
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
            onClick={() => { setShowModal(false); setSearchQuery(''); }}
          />
          <div className="fixed inset-x-4 bottom-0 md:inset-auto md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:w-full md:max-w-lg bg-slate-900 border border-slate-700 rounded-t-2xl md:rounded-2xl shadow-2xl z-50 max-h-[85vh] flex flex-col">
            <div className="p-4 border-b border-slate-700">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-bold text-white flex items-center gap-2">
                  📂 Semua Kategori
                  <span className="text-xs font-normal text-slate-400 bg-slate-800 px-2 py-0.5 rounded">
                    {categories.length} kategori
                  </span>
                </h3>
                <button
                  onClick={() => { setShowModal(false); setSearchQuery(''); }}
                  className="p-2 hover:bg-slate-800 rounded-lg transition-colors text-slate-400 hover:text-white"
                >
                  ✕
                </button>
              </div>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">🔍</span>
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Cari kategori..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all text-sm"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                  >
                    ✕
                  </button>
                )}
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              {filteredCategories.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <div className="text-4xl mb-2">🔍</div>
                  <p>Tidak ada kategori yang cocok</p>
                  <p className="text-sm mt-1">Coba kata kunci lain</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {filteredCategories.map(cat => (
                    <button
                      key={cat.value}
                      onClick={() => handleSelect(cat.value)}
                      className={`p-3 rounded-xl text-left transition-all ${
                        selected === cat.value
                          ? `${platformConfig.bg} text-white shadow-lg`
                          : 'bg-slate-800 hover:bg-slate-700 text-slate-300 border border-slate-700 hover:border-slate-600'
                      }`}
                    >
                      <div className="text-2xl mb-1">{cat.icon}</div>
                      <div className="font-medium text-sm truncate">{cat.label}</div>
                      {selected === cat.value && (
                        <div className="mt-1 text-xs opacity-75">✓ Dipilih</div>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="p-3 border-t border-slate-700 bg-slate-800/50 rounded-b-2xl">
              <div className="flex items-center justify-between text-xs text-slate-400">
                <span>
                  Menampilkan {filteredCategories.length} dari {categories.length} kategori
                </span>
                {searchQuery && (
                  <span className="text-amber-400">Filter aktif</span>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}
